// ============================================
// RÉPONSES TYPES DE DÉPART
// ============================================
// Les 14 réponses rapides qui étaient écrites en dur dans l'admin, reprises
// pour être enregistrées en base (POST /admin/canned-responses) depuis
// Paramètres > Réponses types. Variables limitées à celles que l'admin sait
// remplir : {{customerName}}, {{ticketRef}}, {{agentName}} (l'ancienne
// {{ticketTitle}} est remplacée par la référence du dossier). Le raccourci
// d'origine est gardé en étiquette : « /ar » retrouve l'accusé de réception.
// L'import est rejouable : une réponse dont le titre existe déjà est ignorée.

import type { CannedResponse } from '../../types';

export type ModeleReponse = Omit<CannedResponse, 'id'>;

const SIGNATURE = '\n\nCordialement,\nÉquipe SAV KLY';

export const REPONSES_TYPES_DEPART: ModeleReponse[] = [
  {
    title: 'Accusé de réception',
    tags: ['ar', 'accueil', 'réception'],
    content: 'Bonjour {{customerName}},\n\nNous avons bien reçu votre demande (dossier {{ticketRef}}) et la traitons en priorité.\n\nUn technicien vous contactera sous peu.' + SIGNATURE,
  },
  {
    title: 'Prise en charge',
    tags: ['pc', 'accueil', 'prise en charge'],
    content: 'Bonjour {{customerName}},\n\nJe prends en charge votre demande immédiatement. Je vais analyser la situation et vous recontacte très rapidement.' + SIGNATURE,
  },
  {
    title: "Demande d'informations générales",
    tags: ['di', 'information', 'détails'],
    content: "Bonjour {{customerName}},\n\nPour traiter votre demande efficacement, pourriez-vous nous fournir les informations suivantes :\n\n• Numéro de série de l'équipement\n• Description détaillée du problème\n• Photos si possible\n\nMerci de votre coopération." + SIGNATURE,
  },
  {
    title: 'Demande numéro de série',
    tags: ['ns', 'série', 'identification'],
    content: "Bonjour {{customerName}},\n\nPour identifier précisément votre équipement, pourriez-vous nous communiquer le numéro de série ?\n\nVous le trouverez généralement sur une étiquette à l'arrière ou sous l'appareil." + SIGNATURE,
  },
  {
    title: 'Demande de photos',
    tags: ['ph', 'photos', 'images'],
    content: "Bonjour {{customerName}},\n\nPour mieux comprendre le problème, pourriez-vous nous envoyer quelques photos :\n\n• Une vue d'ensemble de l'équipement\n• Un gros plan sur la zone défaillante\n• Le code erreur affiché (si applicable)\n\nCela nous aidera à préparer l'intervention." + SIGNATURE,
  },
  {
    title: 'Intervention planifiée',
    tags: ['ip', 'intervention', 'planification'],
    content: "Bonjour {{customerName}},\n\nSuite à votre demande, une intervention a été planifiée.\n\nNotre technicien vous contactera pour confirmer le créneau horaire qui vous convient.\n\nMerci de vous assurer que l'équipement est accessible." + SIGNATURE,
  },
  {
    title: 'Solution à tester',
    tags: ['st', 'solution', 'redémarrage'],
    content: "Bonjour {{customerName}},\n\nVoici une solution à tester avant notre intervention :\n\n1. Éteignez complètement l'équipement\n2. Attendez 30 secondes\n3. Redémarrez l'appareil\n\nSi le problème persiste, merci de nous le signaler." + SIGNATURE,
  },
  {
    title: 'Pièce en commande',
    tags: ['cmd', 'commande', 'pièce', 'délai'],
    content: "Bonjour {{customerName}},\n\nSuite au diagnostic effectué, nous avons commandé la pièce nécessaire à la réparation.\n\nDélai de réception estimé : 3 à 5 jours ouvrés.\n\nNous vous recontacterons dès réception pour planifier l'intervention." + SIGNATURE,
  },
  {
    title: 'Demande de garantie',
    tags: ['gar', 'garantie', 'facture'],
    content: "Bonjour {{customerName}},\n\nPour vérifier la prise en charge sous garantie, pourriez-vous nous fournir :\n\n• La facture d'achat ou le bon de livraison\n• La date d'installation de l'équipement\n\nMerci." + SIGNATURE,
  },
  {
    title: 'Dossier résolu',
    tags: ['ok', 'résolu', 'clôture'],
    content: "Bonjour {{customerName}},\n\nVotre demande a été traitée.\n\nN'hésitez pas à nous recontacter si vous avez d'autres questions.\n\nMerci de votre confiance." + SIGNATURE,
  },
  {
    title: 'Intervention terminée',
    tags: ['it', 'intervention', 'terminée'],
    content: "Bonjour {{customerName}},\n\nL'intervention sur votre équipement a été réalisée.\n\nVotre appareil est de nouveau opérationnel. Nous vous invitons à le tester et à nous signaler tout dysfonctionnement.\n\nMerci de votre confiance." + SIGNATURE,
  },
  {
    title: 'Demande de retour',
    tags: ['fb', 'feedback', 'satisfaction'],
    content: "Bonjour {{customerName}},\n\nNous espérons que notre intervention vous a donné satisfaction.\n\nVotre avis nous est utile : n'hésitez pas à nous faire part de vos retours.\n\nMerci de votre confiance." + SIGNATURE,
  },
  {
    title: 'Escalade technique',
    tags: ['et', 'escalade', 'expert'],
    content: "Bonjour {{customerName}},\n\nVotre dossier nécessite une analyse approfondie par notre équipe technique spécialisée.\n\nJe transmets votre demande à nos experts, qui vous recontacteront dans les plus brefs délais.\n\nMerci de votre patience." + SIGNATURE,
  },
  {
    title: 'Retard intervention',
    tags: ['ri', 'retard', 'excuse'],
    content: "Bonjour {{customerName}},\n\nJe vous prie de nous excuser pour ce retard dans le traitement de votre demande.\n\nNous mettons tout en œuvre pour résoudre votre problème au plus vite. Un responsable vous contactera personnellement.\n\nMerci de votre compréhension." + SIGNATURE,
  },
];

const cleTitre = (t: string) => t.trim().toLocaleLowerCase('fr-FR');

/** Modèles de départ absents de la base (comparaison sur le titre, sans casse). */
export function modelesAImporter(existantes: Array<Pick<CannedResponse, 'title'>>, modeles = REPONSES_TYPES_DEPART): ModeleReponse[] {
  const titres = new Set(existantes.map(r => cleTitre(r.title)));
  return modeles.filter(m => !titres.has(cleTitre(m.title)));
}

/** Étiquettes saisies « a, b ,c » → ['a', 'b', 'c'] (vides et doublons retirés). */
export function lireEtiquettes(saisie: string): string[] {
  return [...new Set(saisie.split(',').map(t => t.trim()).filter(Boolean))];
}
